import {
  useQuery,
  useMutation,
  useQueryClient,
  UseQueryResult,
} from "@tanstack/react-query";
import axios from "axios";
import { Student } from "./useStudents";
import { MARKSHEET_TYPE_OPTIONS } from '../components/enum/MarksheetTypeOptions';
import { useTranslation } from "react-i18next";
import type { AssessmentType, Grade } from "../utils/gradeDisplay";

export type GradeCategory = keyof typeof Grade | string;

// Define interfaces for the subject in marksheet request
export interface MarksheetSubjectRequestDto {
  subjectCode: string;
  subjectName: string;
  theoryMarks: number | null;
  practicalMarks: number | null;
  originalPracticalMarks?: number;
  remark?: string;
}

// Define interfaces for the subject in marksheet response
export interface MarksheetSubjectResponseDto {
  subjectCode: string;
  subjectName: string;
  theoryMarks: number | null;
  practicalMarks: number | null;
  quarterlyWeightage?: number;
  halfYearlyWeightage?: number;
  originalPracticalMarks?: number;
  remark?: string;
}

// Define interface for the request DTO
export interface MarksheetRequestDto {
  id?: number; // Optional, for updates
  studentId: number;
  schoolId: number;
  classId: number;
  marksheetType: string; // QUARTERLY, HALF_YEARLY, etc.
  sessionYear: string;
  sessionId: number;
  className: string;
  subjects: MarksheetSubjectRequestDto[];
  monthType: string; // Required field in backend
  published?: boolean;
  marksheet_assessment?: Partial<Record<AssessmentType, GradeCategory>>;
}

// Define interface for the response DTO
export interface MarksheetResponseDto {
  id: number;
  student: Student;
  schoolId: number;
  schoolName: string;
  marksheetType: string;
  sessionYear: string;
  className: string;
  classId: number;
  subjects: MarksheetSubjectResponseDto[];
  percentage: number;
  totalMarks?: number;
  totalGrade: GradeCategory; // GradeCategory enum from backend
  result: string;
  rank: number;
  failSubjects: string;
  sessionId: number;
  promote: string; // PromotionType enum
  monthType: string; // Required field in backend
  published?: boolean; // New field for publish status
  marksheet_assessment?: Partial<Record<AssessmentType, GradeCategory>>;
}

export interface MarksheetPublishRequestDto {
  marksheetId: number;
  studentCode: string;
  published: boolean;
}

// Type for marksheet type based on existing options
export type MarksheetType = typeof MARKSHEET_TYPE_OPTIONS[number]['value'];

// Interface for Excel export request
export interface ExcelExportRequestDto {
  schoolId: number;
  classId: number;
  sessionId: number;
  marksheetType: MarksheetType;
  monthType: string;
}

// Interface for Excel import request
export interface ExcelImportRequestDto {
  file: File;
  schoolId: number;
  classId: number;
  sessionId: number;
  sessionYear: string;
  marksheetType: string;
  monthType: string;
}

// Update the request DTO
export interface MeritlistRequestDto {
  schoolId: number;
  classId: number | null;
  className: string;
  sessionId: number;
  marksheetType: string;
}

// Interface for delete marksheet request
export interface DeleteMarksheetRequestDto {
  schoolId: number;
  classId: number;
  sessionId: number;
  studentId: number;
  marksheetType: string;
  monthType: string; // Required field for delete operation
}

// Interface for download marksheet request
export interface DownloadMarksheetRequestDto {
  classId: number;
  sessionId: number;
  marksheetType: string;
  monthType: string; // Required for all marksheet types
  sahshekshik?: boolean;
}

export interface MeritlistResponseDto {
  // Define according to your backend DTO
  // Example:
  studentId: number;
  studentName: string;
  totalMarks: number;
  // ...other fields
  promote: string;
  className: string;
  session: string;
  monthType?: string;
  result?: string; // Marksheet result (PASS/FAIL) from backend
  percentage?: number; // Percentage from marksheet
}

// Interface for subject marks DTO in marks entry
export interface SubjectMarksDto {
  studentId: number;
  subjectCode: string;
  theory: number;
  practical: number;
}

// Interface for marks entry DTO
export interface MarksheetEntryDto {
  marksheetType: string;
  monthType: string;
  classId: number;
  sessionId: number;
  subjectMarksDtoList: SubjectMarksDto[];
}

// Interface for marks entry response
export interface MarksheetEntryResponse {
  saved: any[];
  errors: string[];
}

const API_URL = `${process.env.REACT_APP_API_URL}/marksheets`;
const MARKS_ENTRY_API_URL = `${process.env.REACT_APP_API_URL}/marks-entry`;

export interface MarksheetByClassKeyAndSessionIdAndMarksheetTypeParams {
  classId?:number;
  className?:string;
  sessionId:number;
  marksheetType:string;
  page?:number;
  size?:number;
  sort?:PageableSort;
}

export const getMarksheetByClassKeyAndSessionIdAndMarksheetType = async (
  params: MarksheetByClassKeyAndSessionIdAndMarksheetTypeParams
): Promise<PageResponseDto<MarksheetResponseDto>> => {
  const response = await axios.get(`${API_URL}/byMarksheetType`, {
    params: {
      ...(params.classId ? { classId: params.classId } : {}),
      ...(params.className ? { className: params.className } : {}),
      sessionId: params.sessionId,
      marksheetType: params.marksheetType,
      page: params.page ?? 0,
      size: params.size ?? 10,
      ...(params.sort ? { sort: params.sort } : {}),
    },
  });
  return response.data;
}

/**
 * GET /marksheets/classKey/sessionId?classId=&className=&sessionId=
 * Backend returns List<MarksheetResponseDto>.
 */
export const getMarksheetsByClassKeyAndSessionId = async (params: {
  classId?: number;
  className?: string; 
  sessionId: number;
}): Promise<MarksheetResponseDto[]> => {
  const response = await axios.get(`${API_URL}/classKeys/sessionId`, {
    params: {
      ...(params.classId != null ? { classId: params.classId } : {}),
      ...(params.className ? { className: params.className } : {}),
      sessionId: params.sessionId,
    },
  });
  const data = response.data as any;
  // Backend might return List<T>, Page<T> (with `.content`), or a single object.
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.content)) return data.content;
  return data ? [data] : [];
};

const publishMarksheet = async (data: MarksheetPublishRequestDto): Promise<MarksheetResponseDto> => {
  const response = await axios.put(`${API_URL}/publish`, data);
  return response.data;
};

const publishBulkMarksheets = async (data: MarksheetPublishRequestDto[]): Promise<MarksheetResponseDto[]> => {
  const response = await axios.put(`${API_URL}/publish-bulk`, data);
  return response.data;
};

export type PageableSort = `${string},${"asc" | "desc"}` | string;

export interface PageResponseDto<T> {
  content: T[];
  totalPages: number;
  totalElements: number;
  size: number;
  number: number; // current page index (0-based)
  // other spring page fields may exist but we don't need them
}

export interface MarksheetsPageParams {
  page?: number;
  size?: number;
  sort?: PageableSort;
}

export interface MarksheetsByClassKeyAndSessionIdParams extends MarksheetsPageParams {
  classId?: number;
  className?: string;
  sessionId: number;
}

export interface SearchMarksheetsByNameParams {
  classId?: number;
  className?: string;
  sessionId: number;
  name: string;
  page?: number;
  size?: number;
  sort?: PageableSort;
}

/**
 * GET /marksheets/classKey/sessionId?classId=&className=&sessionId=&page=&size=&sort=
 * Backend returns Spring `Page<MarksheetResponseDto>`.
 */
export const getMarksheetsPageByClassKeyAndSessionId = async (
  params: MarksheetsByClassKeyAndSessionIdParams
): Promise<PageResponseDto<MarksheetResponseDto>> => {
  const response = await axios.get(`${API_URL}/classKey/sessionId`, {
    params: {
      ...(params.classId != null ? { classId: params.classId } : {}),
      ...(params.className ? { className: params.className } : {}),
      sessionId: params.sessionId,
      page: params.page ?? 0,
      size: params.size ?? 10,
      ...(params.sort ? { sort: params.sort } : {}),
    },
  });
  return response.data;
};

/**
 * GET /marksheets/search?classId=&className=&sessionId=&name=&page=&size=&sort=
 * Backend returns Spring `Page<MarksheetResponseDto>`.
 */
export const searchMarksheetsByName = async (
  params: SearchMarksheetsByNameParams
): Promise<PageResponseDto<MarksheetResponseDto>> => {
  const response = await axios.get(`${API_URL}/search`, {
    params: {
      ...(params.classId != null ? { classId: params.classId } : {}),
      ...(params.className ? { className: params.className } : {}),
      sessionId: params.sessionId,
      name: params.name,
      page: params.page ?? 0,
      size: params.size ?? 10,
      ...(params.sort ? { sort: params.sort } : {}),
    },
  });
  return response.data;
};

/**
 * Convenience: fetch ALL pages and return a flat array.
 * Keeps existing UI code working (it expects `MarksheetResponseDto[]`).
 */
export const getAllMarksheetsByClassKeyAndSessionId = async (
  params: Omit<MarksheetsByClassKeyAndSessionIdParams, "page">,
  opts: Omit<MarksheetsPageParams, "page"> & { maxPages?: number } = {}
): Promise<MarksheetResponseDto[]> => {
  const size = opts.size ?? 200;
  const sort = opts.sort;
  const maxPages = opts.maxPages ?? 50;

  const out: MarksheetResponseDto[] = [];
  let page = 0;
  while (page < maxPages) {
    const res = await getMarksheetsPageByClassKeyAndSessionId({
      ...params,
      page,
      size,
      sort,
    });
    const content = Array.isArray(res?.content) ? res.content : [];
    out.push(...content);
    const totalPages = Number(res?.totalPages ?? 0);
    if (page >= totalPages - 1) break;
    if (content.length === 0) break; // safety for weird backend responses
    page += 1;
  }

  return out;
};

/** Back-compat wrapper for callers still passing (classId, sessionId). */
export const getAllMarksheetsByClassIdAndSessionId = async (
  classId: number,
  sessionId: number,
  opts: Omit<MarksheetsPageParams, "page"> & { maxPages?: number } = {}
): Promise<MarksheetResponseDto[]> =>
  getAllMarksheetsByClassKeyAndSessionId({ classId, sessionId }, opts);

const createMarksheet = async (
  data: MarksheetRequestDto,
  t: (key: string) => string
): Promise<MarksheetResponseDto> => {
  if (!data.sessionId) throw new Error(t("session_id_required_marksheet"));

  // Determine endpoint based on marksheet type and class
  let endpoint: string;

  if (data.marksheetType === "MONTHLY") {
    // Monthly marksheets use their own endpoint
    endpoint = `${API_URL}/addMonthlyMarksheet`;
  } else {
    // Check if class is specifically 10th - only 10th uses /add endpoint
    const is10thClass = data.className?.includes("10th");

    if (is10thClass) {
      // Only 10th class uses /add endpoint
      endpoint = `${API_URL}/add`;
    } else {
      // All other classes (1st to 9th, 11th, 12th) use the new /addMarksheet endpoint
      endpoint = `${API_URL}/addMarksheet`;
    }
  }

  // Ensure monthType is included in the payload
  const payload = {
    ...data,
    monthType: data.monthType || 'JANUARY' // Ensure monthType is always present
  };

  const response = await axios.post(endpoint, payload);
  return response.data;
};

const getMarksheetByTypeClassRoll = async (
  marksheetType: string,
  className: string,
  rollNo: string
): Promise<MarksheetResponseDto> => {
  const response = await axios.get(`${API_URL}/by-type-class-roll`, {
    params: { marksheetType, className, rollNo },
  });
  return response.data;
};

// Get marksheets by student ID (returns list of marksheets)
export const getMarksheetByStudentId = async (
  studentId: number
): Promise<MarksheetResponseDto[]> => {
  const response = await axios.get(`${API_URL}/getByStudentId`, {
    params: { studentId },
  });
  // Handle both single object and array responses
  return Array.isArray(response.data) ? response.data : [response.data];
};

// Get marksheets by student code (returns list of marksheets)
const getMarksheetByStudentCode = async (
  studentCode: string
): Promise<MarksheetResponseDto[]> => {
  const response = await axios.get(`${API_URL}/studentCode`, {
    params: { studentCode },
  });
  // Handle both single object and array responses
  return Array.isArray(response.data) ? response.data : [response.data];
};

// NEW: POST /meritlist
const getMeritlist = async (
  data: MeritlistRequestDto
): Promise<MeritlistResponseDto[]> => {
  const response = await axios.post(`${API_URL}/meritlist`, data);
  return response.data;
};

// NEW: POST /tclist
const getTcList = async (
  data: MeritlistRequestDto
): Promise<MeritlistResponseDto[]> => {
  // Check if data has been modified by any middleware
  const cleanData = {
    schoolId: data.schoolId,
    classId: data.classId,
    className: data.className,
    sessionId: data.sessionId,
    marksheetType: data.marksheetType
  };

  const response = await axios.post(`${API_URL}/tclist`, cleanData);
  return response.data;
};

const updateMarksheet = async (
  data: MarksheetRequestDto
): Promise<MarksheetResponseDto> => {
  const response = await axios.put(`${API_URL}/update`, data);
  return response.data;
};

const deleteMarksheet = async (
  data: DeleteMarksheetRequestDto
): Promise<void> => {
  await axios.delete(`${API_URL}/delete`, {
    params: {
      schoolId: data.schoolId,
      classId: data.classId,
      sessionId: data.sessionId,
      studentId: data.studentId,
      marksheetType: data.marksheetType,
      monthType: data.monthType
    }
  });
};

// Excel export function
const exportExcelFile = async (params: ExcelExportRequestDto): Promise<void> => {
  const response = await axios.get(`${API_URL}/export-excel`, {
    params: {
      schoolId: params.schoolId,
      classId: params.classId,
      sessionId: params.sessionId,
      marksheetType: params.marksheetType,
      monthType: params.monthType
    },
    responseType: 'blob', // Important for file downloads
  });

  // Create blob from response data
  const blob = new Blob([response.data], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  });

  // Extract filename from Content-Disposition header or create default
  const contentDisposition = response.headers['content-disposition'];
  let filename = `${params.marksheetType}_${params.monthType}_EX_${params.classId}_${params.sessionId}.xlsx`;

  if (contentDisposition) {
    const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
    if (filenameMatch && filenameMatch[1]) {
      filename = filenameMatch[1].replace(/['"]/g, '');
    }
  }

  // Create download link and trigger download
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

// Excel import function
const importExcelFile = async (params: ExcelImportRequestDto): Promise<{ success: boolean; message: string; errorFile?: Blob }> => {
  const formData = new FormData();
  formData.append('file', params.file);
  formData.append('schoolId', params.schoolId.toString());
  formData.append('classId', params.classId.toString());
  formData.append('sessionId', params.sessionId.toString());
  formData.append('sessionYear', params.sessionYear);
  formData.append('marksheetType', params.marksheetType);
  formData.append('monthType', params.monthType);

  try {
    const response = await axios.post(`${API_URL}/import-excel`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      responseType: 'blob', // Handle both blob (error file) and text (success message)
    });

    // Check if response is an Excel file (error case) or text (success case)
    const contentType = response.headers['content-type'];

    if (contentType && contentType.includes('application/vnd.openxmlformats-officedocument.spreadsheetml.sheet')) {
      // Response is an Excel file with errors
      return {
        success: false,
        message: 'Some entries contain errors. Please download the error file to see the details.',
        errorFile: response.data,
      };
    } else {
      // Response is a success message (text)
      // Convert blob to text
      const text = await response.data.text();
      return {
        success: true,
        message: text || 'Marksheet imported successfully',
      };
    }
  } catch (error: any) {
    // Handle actual errors (500, network errors, etc.)
    let errorMessage = 'Failed to import Excel';

    try {
      // Since we're using responseType: 'blob', error response data is also a Blob
      if (error.response?.data instanceof Blob) {
        const text = await error.response.data.text();
        errorMessage = text || error.message || 'Failed to import Excel';
      } else {
        errorMessage = error.response?.data?.message || error.message || 'Failed to import Excel';
      }
    } catch {
      errorMessage = error.message || 'Failed to import Excel';
    }

    throw new Error(errorMessage);
  }
};

// Download marksheet function
const downloadMarksheet = async (params: DownloadMarksheetRequestDto): Promise<void> => {
  const response = await axios.get(`${API_URL}/download`, {
    params: {
      classId: params.classId,
      sessionId: params.sessionId,
      marksheetType: params.marksheetType,
      monthType: params.monthType,
      sahshekshik: params.sahshekshik ?? false,
    },
    responseType: 'blob', // Important for file downloads
  });

  // Create blob from response data
  const blob = new Blob([response.data], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  });

  // Extract filename from Content-Disposition header or create default
  const contentDisposition = response.headers['content-disposition'];
  let filename = `${params.marksheetType}_${params.monthType}_EX_${params.classId}_${params.sessionId}.xlsx`;

  if (contentDisposition) {
    const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
    if (filenameMatch && filenameMatch[1]) {
      filename = filenameMatch[1].replace(/['"]/g, '');
    }
  }

  // Create download link and trigger download
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

// Add marks entry function
const addMarksEntry = async (data: MarksheetEntryDto): Promise<MarksheetEntryResponse> => {
  const response = await axios.post(`${MARKS_ENTRY_API_URL}/add`, data);
  return response.data;
};

const getAllMarksEntries = async (classId: number, sessionId: number): Promise<MarksheetEntryDto[]> => {
  const response = await axios.get(`${MARKS_ENTRY_API_URL}/get/${classId}/${sessionId}`);
  return response.data;
}

// Delete marks entry function
const deleteMarksEntry = async (data: MarksheetEntryDto): Promise<string> => {
  const response = await axios.delete(`${MARKS_ENTRY_API_URL}/delete`, {
    data: data,
  });
  return response.data;
};

export function useMarkSheets(
  tenantLoading: boolean = false,
  disableMarksheetsQuery: boolean = false,
  marksheetsClassId?: number | null,
  marksheetsSessionId?: number | null,
  /** If set, use server paging for marksheets list (no multi-page prefetch). */
  marksheetsPageIndex?: number | null,
  marksheetsPageSize?: number | null,
  marksheetsUseServerPaging: boolean = false,
  /** If set, marksheets list will be fetched by className (supports 9th-ALL, 10th-ALL, etc.). */
  marksheetsClassName?: string | null,
  /** If set (and server paging is on), fetch only selected marksheetType. */
  marksheetsFilterMarksheetType?: string | null
) {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const classIdOk =
    marksheetsClassId != null && Number(marksheetsClassId) > 0;
  const classNameOk =
    marksheetsClassName != null && String(marksheetsClassName).trim().length > 0;
  const sessionIdOk =
    marksheetsSessionId != null && Number(marksheetsSessionId) > 0;

  const pageIndexOk =
    marksheetsPageIndex != null && Number(marksheetsPageIndex) >= 0;
  const pageSizeOk =
    marksheetsPageSize != null && Number(marksheetsPageSize) > 0;

  const filterTypeOk =
    marksheetsFilterMarksheetType != null &&
    String(marksheetsFilterMarksheetType).trim().length > 0;

  const marksheetsPageQuery = useQuery<PageResponseDto<MarksheetResponseDto>, Error>({
    queryKey: [
      "marksheetsPage",
      marksheetsClassId ?? null,
      marksheetsClassName ?? null,
      marksheetsSessionId ?? null,
      marksheetsPageIndex ?? null,
      marksheetsPageSize ?? null,
    ],
    queryFn: () =>
      getMarksheetsPageByClassKeyAndSessionId({
        ...(classNameOk ? { className: String(marksheetsClassName) } : { classId: Number(marksheetsClassId) }),
        sessionId: Number(marksheetsSessionId),
        page: Number(marksheetsPageIndex),
        size: Number(marksheetsPageSize),
      }),
    enabled:
      !tenantLoading &&
      !disableMarksheetsQuery &&
      marksheetsUseServerPaging &&
      !filterTypeOk &&
      (classIdOk || classNameOk) &&
      sessionIdOk &&
      pageIndexOk &&
      pageSizeOk,
  });

  const marksheetsByTypePageQuery = useQuery<PageResponseDto<MarksheetResponseDto>, Error>({
    queryKey: [
      "marksheetsPage",
      "byType",
      marksheetsClassId ?? null,
      marksheetsClassName ?? null,
      marksheetsSessionId ?? null,
      marksheetsFilterMarksheetType ?? null,
      marksheetsPageIndex ?? null,
      marksheetsPageSize ?? null,
    ],
    queryFn: () =>
      getMarksheetByClassKeyAndSessionIdAndMarksheetType({
        ...(classNameOk ? { className: String(marksheetsClassName) } : { classId: Number(marksheetsClassId) }),
        sessionId: Number(marksheetsSessionId),
        marksheetType: String(marksheetsFilterMarksheetType),
        page: Number(marksheetsPageIndex),
        size: Number(marksheetsPageSize),
      }),
    enabled:
      !tenantLoading &&
      !disableMarksheetsQuery &&
      marksheetsUseServerPaging &&
      filterTypeOk &&
      (classIdOk || classNameOk) &&
      sessionIdOk &&
      pageIndexOk &&
      pageSizeOk,
  });

  const marksheetsQuery = useQuery<MarksheetResponseDto[], Error>({
    queryKey: [
      "marksheets",
      marksheetsClassId ?? null,
      marksheetsClassName ?? null,
      marksheetsSessionId ?? null,
    ],
    queryFn: () =>
      getAllMarksheetsByClassKeyAndSessionId({
        ...(classNameOk ? { className: String(marksheetsClassName) } : { classId: Number(marksheetsClassId) }),
        sessionId: Number(marksheetsSessionId),
      }),
    enabled:
      !tenantLoading &&
      !disableMarksheetsQuery &&
      !marksheetsUseServerPaging &&
      (classIdOk || classNameOk) &&
      sessionIdOk,
  });

  const marksheetsByClassIdAndSessionIdQuery = useQuery<MarksheetResponseDto[], Error>({
    queryKey: [
      "marksheets",
      "classKeySessionId",
      marksheetsClassId ?? null,
      marksheetsClassName ?? null,
      marksheetsSessionId ?? null,
    ],
    queryFn: () =>
      getMarksheetsByClassKeyAndSessionId({
        ...(classNameOk ? { className: String(marksheetsClassName) } : { classId: Number(marksheetsClassId) }),
        sessionId: Number(marksheetsSessionId),
      }),
    enabled:
      !tenantLoading &&
      !disableMarksheetsQuery &&
      (classIdOk || classNameOk) &&
      sessionIdOk,
  });

  const invalidateStudentMarksheetsCache = (studentId?: number | null) => {
    if (studentId != null && Number(studentId) > 0) {
      queryClient.invalidateQueries({
        queryKey: ["marksheet", "studentId", studentId],
      });
    } else {
      queryClient.invalidateQueries({ queryKey: ["marksheet", "studentId"] });
    }
  };

  const invalidatePaginatedMarksheetsGets = () => {
    queryClient.invalidateQueries({
      predicate: (query) => {
        const rootKey = query.queryKey?.[0];
        return rootKey === "marksheetsPage" || rootKey === "marksheetsSearchByName";
      },
    });
  };

  // Mutation for creating a new marksheet
  const createMarksheetMutation = useMutation({
    mutationFn: (data: MarksheetRequestDto) => createMarksheet(data, t),
    onSuccess: (_data, variables) => {
      invalidateStudentMarksheetsCache(variables?.studentId);
      invalidatePaginatedMarksheetsGets();
      queryClient.invalidateQueries({ queryKey: ["marksheets"] });
      queryClient.invalidateQueries({
        queryKey: ["marksheets", marksheetsClassId ?? null, marksheetsSessionId ?? null],
      });
      queryClient.invalidateQueries({
        queryKey: [
          "marksheetsPage",
          marksheetsClassId ?? null,
          marksheetsSessionId ?? null,
        ],
      });
      queryClient.invalidateQueries({ queryKey: ["tclist"] }); // Add this to refresh promote student list
      queryClient.invalidateQueries({ queryKey: ["meritlist"] }); // Add this to refresh meritlist
      queryClient.invalidateQueries({ queryKey: ["allGosvaraData"] }); // Add this to refresh gosvara data
      queryClient.invalidateQueries({ queryKey: ["gosvaraData"] }); // Add this to refresh gosvara data
      queryClient.invalidateQueries({ queryKey: ["monthlyGosvara"] }); // Add this to refresh monthly gosvara
    },
  });

  // Mutation for updating a marksheet
  const updateMarksheetMutation = useMutation({
    mutationFn: updateMarksheet,
    onSuccess: (_data, variables) => {
      invalidateStudentMarksheetsCache(variables?.studentId);
      invalidatePaginatedMarksheetsGets();
      queryClient.invalidateQueries({ queryKey: ["marksheets"] });
      queryClient.invalidateQueries({
        queryKey: ["marksheets", marksheetsClassId ?? null, marksheetsSessionId ?? null],
      });
      queryClient.invalidateQueries({
        queryKey: [
          "marksheetsPage",
          marksheetsClassId ?? null,
          marksheetsSessionId ?? null,
        ],
      });
      queryClient.invalidateQueries({ queryKey: ["tclist"] }); // Add this to refresh promote student list
      queryClient.invalidateQueries({ queryKey: ["meritlist"] }); // Add this to refresh meritlist
      queryClient.invalidateQueries({ queryKey: ["allGosvaraData"] }); // Add this to refresh gosvara data
      queryClient.invalidateQueries({ queryKey: ["gosvaraData"] }); // Add this to refresh gosvara data
      queryClient.invalidateQueries({ queryKey: ["monthlyGosvara"] }); // Add this to refresh monthly gosvara
    },
  });

  // Mutation for deleting a marksheet
  const deleteMarksheetMutation = useMutation({
    mutationFn: deleteMarksheet,
    onSuccess: (_data, variables) => {
      invalidateStudentMarksheetsCache(variables?.studentId);
      queryClient.invalidateQueries({ queryKey: ["marksheets"] });
      queryClient.invalidateQueries({
        queryKey: ["marksheets", marksheetsClassId ?? null, marksheetsSessionId ?? null],
      });
      queryClient.invalidateQueries({
        queryKey: [
          "marksheetsPage",
          marksheetsClassId ?? null,
          marksheetsSessionId ?? null,
        ],
      });
      queryClient.invalidateQueries({ queryKey: ["tclist"] }); // Add this to refresh promote student list
      queryClient.invalidateQueries({ queryKey: ["meritlist"] }); // Add this to refresh meritlist
      queryClient.invalidateQueries({ queryKey: ["allGosvaraData"] }); // Add this to refresh gosvara data
      queryClient.invalidateQueries({ queryKey: ["gosvaraData"] }); // Add this to refresh gosvara data
      queryClient.invalidateQueries({ queryKey: ["monthlyGosvara"] }); // Add this to refresh monthly gosvara
    },
  });

  // Mutation for Excel export
  const exportExcelMutation = useMutation({
    mutationFn: exportExcelFile,
    onError: (error) => {
    },
  });

  // Mutation for Excel import
  const importExcelMutation = useMutation({
    mutationFn: importExcelFile,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["marksheets"] });
      queryClient.invalidateQueries({
        queryKey: ["marksheets", marksheetsClassId ?? null, marksheetsSessionId ?? null],
      });
      queryClient.invalidateQueries({
        queryKey: ["marksheetsPage", marksheetsClassId ?? null, marksheetsSessionId ?? null],
      });
      queryClient.invalidateQueries({ queryKey: ["tclist"] });
      queryClient.invalidateQueries({ queryKey: ["meritlist"] });
      queryClient.invalidateQueries({ queryKey: ["allGosvaraData"] });
      queryClient.invalidateQueries({ queryKey: ["gosvaraData"] });
      queryClient.invalidateQueries({ queryKey: ["monthlyGosvara"] });
    },
    onError: (error) => {
    },
  });

  // Mutation for download marksheet
  const downloadMarksheetMutation = useMutation({
    mutationFn: downloadMarksheet,
    onError: (error) => {
    },
  });

  // Mutation for adding marks entry
  const addMarksEntryMutation = useMutation({
    mutationFn: addMarksEntry,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["marksheets"] });
      queryClient.invalidateQueries({ queryKey: ["marksEntries"] });
      queryClient.invalidateQueries({ queryKey: ["tclist"] });
      queryClient.invalidateQueries({ queryKey: ["meritlist"] });
      queryClient.invalidateQueries({ queryKey: ["allGosvaraData"] });
      queryClient.invalidateQueries({ queryKey: ["gosvaraData"] });
      queryClient.invalidateQueries({ queryKey: ["monthlyGosvara"] });
    },
  });

  // Mutation for deleting marks entry
  const deleteMarksEntryMutation = useMutation({
    mutationFn: deleteMarksEntry,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["marksEntries"] });
      queryClient.invalidateQueries({ queryKey: ["marksheets"] });
    },
  });

  const publishMarksheetMutation = useMutation({
    mutationFn: publishMarksheet,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["marksheets"] });
      queryClient.invalidateQueries({ queryKey: ["marksheet"] });
    },
  });

  const publishBulkMarksheetsMutation = useMutation({
    mutationFn: publishBulkMarksheets,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["marksheets"] });
      queryClient.invalidateQueries({ queryKey: ["marksheet"] });
    },
  });

  // Query for fetching a marksheet by type, class, and rollNo
  const useMarksheetByTypeClassRoll = (
    marksheetType: string,
    className: string,
    rollNo: string,
    enabled: boolean = true
  ): UseQueryResult<MarksheetResponseDto, Error> =>
    useQuery<MarksheetResponseDto, Error>({
      queryKey: ["marksheet", marksheetType, className, rollNo],
      queryFn: () =>
        getMarksheetByTypeClassRoll(marksheetType, className, rollNo),
      enabled,
    });

  // Query for meritlist
  const useMeritlist = (
    meritlistRequest: MeritlistRequestDto,
    enabled: boolean = true
  ): UseQueryResult<MeritlistResponseDto[], Error> =>
    useQuery<MeritlistResponseDto[], Error>({
      queryKey: ["meritlist", meritlistRequest],
      queryFn: () => getMeritlist(meritlistRequest),
      enabled,
    });

  // Query for tclist
  const useTcList = (
    tcListRequest: MeritlistRequestDto,
    enabled: boolean = true
  ): UseQueryResult<MeritlistResponseDto[], Error> =>
    useQuery<MeritlistResponseDto[], Error>({
      queryKey: ["tclist", tcListRequest],
      queryFn: () => getTcList(tcListRequest),
      enabled,
    });

  // Query for getting marksheets by student ID (returns list)
  const useMarksheetByStudentId = (
    studentId: number | null,
    enabled: boolean = true
  ): UseQueryResult<MarksheetResponseDto[], Error> =>
    useQuery<MarksheetResponseDto[], Error>({
      queryKey: ["marksheet", "studentId", studentId],
      queryFn: () => getMarksheetByStudentId(studentId!),
      enabled: enabled && !!studentId && !tenantLoading,
    });

  // Query for getting marksheets by student code (returns list)
  const useMarksheetByStudentCode = (
    studentCode: string | null | undefined,
    enabled: boolean = true
  ): UseQueryResult<MarksheetResponseDto[], Error> =>
    useQuery<MarksheetResponseDto[], Error>({
      queryKey: ["marksheet", "studentCode", studentCode],
      queryFn: () => getMarksheetByStudentCode(studentCode!),
      enabled: enabled && !!studentCode && !tenantLoading,
    });

  const useAllMarksEntries = (classId: number | null, sessionId: number | null, enabled: boolean = true): UseQueryResult<MarksheetEntryDto[], Error> =>
    useQuery<MarksheetEntryDto[], Error>({
      queryKey: ["marksEntries", classId, sessionId],
      queryFn: () => getAllMarksEntries(classId!, sessionId!),
      enabled: enabled && !!classId && !!sessionId && !tenantLoading
    });

  const useSearchMarksheetsByName = (
    searchParams: SearchMarksheetsByNameParams | null | undefined,
    enabled: boolean = true
  ): UseQueryResult<PageResponseDto<MarksheetResponseDto>, Error> =>
    useQuery<PageResponseDto<MarksheetResponseDto>, Error>({
      queryKey: ["marksheetsSearchByName", searchParams ?? null],
      queryFn: () => searchMarksheetsByName(searchParams!),
      enabled:
        enabled &&
        !tenantLoading &&
        !!searchParams &&
        Number(searchParams.sessionId) > 0 &&
        String(searchParams.name || "").trim().length > 0,
    });


  return {
    marksheetsQuery,
    marksheetsPageQuery,
    marksheetsByTypePageQuery,
    marksheetsByClassIdAndSessionIdQuery,
    createMarksheetMutation,
    updateMarksheetMutation,
    deleteMarksheetMutation,
    exportExcelMutation,
    importExcelMutation,
    downloadMarksheetMutation,
    addMarksEntryMutation,
    deleteMarksEntryMutation,
    useMarksheetByTypeClassRoll,
    useMeritlist,
    useTcList,
    useMarksheetByStudentId,
    useMarksheetByStudentCode,
    useAllMarksEntries,
    useSearchMarksheetsByName,
    publishMarksheetMutation,
    publishBulkMarksheetsMutation,
    // Export marksheet type options for easy access
    marksheetTypeOptions: MARKSHEET_TYPE_OPTIONS,
  };
}